import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Search, Bookmark, BookmarkCheck, ExternalLink, Globe2, MapPin, Phone, Star } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import WorldMap from "../components/WorldMap";
import PaywallGate from "../components/PaywallGate";

export default function Agencies() {
  const { user, refreshUser } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [countries, setCountries] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({ total: 0, categories: [] });
  const [country, setCountry] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const saved = new Set(user?.saved_agencies || []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (country !== "all") params.country = country;
      if (q.trim()) params.q = q.trim();
      if (category !== "all") params.category = category;
      const { data } = await api.get("/agencies", { params });
      setAgencies(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [c, f, cc] = await Promise.all([
        api.get("/agencies/countries"),
        api.get("/agencies", { params: { featured: true } }),
        api.get("/agencies/categories/counts"),
      ]);
      setCountries(c.data);
      setFeatured(f.data);
      setCategoryCounts(cc.data);
    })();
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [country, category]);

  const onSearch = (e) => {
    e.preventDefault();
    load();
  };

  const toggleSave = async (id) => {
    try {
      const { data } = await api.post("/agencies/save", { agency_id: id });
      toast.success(data.action === "saved" ? "Added to your favorites" : "Removed from favorites");
      await refreshUser();
    } catch {
      toast.error("Action failed");
    }
  };

  const isFiltering = q.trim() || country !== "all" || category !== "all";

  const directoryRef = useRef(null);

  const handleMapCountryClick = (alpha2) => {
    setCountry(alpha2);
    setTimeout(() => {
      directoryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const availableCountryCodes = countries.map((c) => c.country_code).filter((c) => c && c !== "INTL");
  const currentCountryName = countries.find((c) => c.country_code === country)?.country;

  return (
    <div className="min-h-screen bg-[#0A0C10] pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="overline" data-testid="agencies-overline">Global Registry · OSRR</div>
            <h1 className="font-display text-4xl lg:text-6xl tracking-tight mt-3 leading-[1.05]">
              <span className="text-[#7DA3D9]">200+</span> Musical Rights Agencies
              <br />
              Across the World
            </h1>
          </div>
          <div className="lg:col-span-5 flex items-end">
            <p className="text-slate-400 leading-relaxed">
              The OSRR directory indexes the global network of Performing Rights Organizations,
              collection societies, music publishers, and intellectual property authorities.
              Search any of the <span className="text-[#B8D0EE] font-medium">200+ agencies</span> by
              name, or filter by country to find your territory.
            </p>
          </div>
        </div>

        {/* Stats banner */}
        <div className="glass-card p-6 mb-12 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat value="200+" label="Agencies worldwide" />
          <Stat value="120" label="Countries covered" />
          <Stat value="4M+" label="Creators represented" />
          <Stat value="6" label="Featured resources" />
        </div>

        {/* Featured Resources */}
        {featured.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center gap-2 mb-2">
              <Star className="w-4 h-4 text-[#7DA3D9]" fill="#7DA3D9" />
              <div className="overline">Featured Resources</div>
            </div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight">
              Essential rights authorities for every catalog
            </h2>
            <p className="text-slate-400 mt-3 max-w-2xl">
              Six priority resources every composer and songwriter should know — global IP authorities,
              international royalty administrators, and U.S. federal protection bodies.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-8" data-testid="featured-grid">
              {featured.map((a) => (
                <FeaturedCard
                  key={a.id}
                  agency={a}
                  saved={saved.has(a.id)}
                  onToggleSave={() => toggleSave(a.id)}
                />
              ))}
            </div>
          </section>
        )}

        <div className="divider-gold mb-10" />

        {/* Interactive World Map */}
        <section className="mb-16" data-testid="world-map-section">
          <div className="flex items-center gap-2 mb-2">
            <Globe2 className="w-4 h-4 text-[#7DA3D9]" />
            <div className="overline">Explore by Territory</div>
          </div>
          <h2 className="font-display text-3xl lg:text-4xl tracking-tight">
            Click a country to find its rights agencies
          </h2>
          <p className="text-slate-400 mt-3 max-w-2xl">
            Highlighted territories have one or more agencies registered in OSRR.
            Tap any glowing country to filter the directory below.
            {currentCountryName && country !== "all" && (
              <>
                {" "}Currently viewing{" "}
                <span className="text-[#B8D0EE] font-medium">{currentCountryName}</span>.
              </>
            )}
          </p>
          <div className="glass-card p-4 lg:p-6 mt-8">
            <WorldMap
              availableCountryCodes={availableCountryCodes}
              selectedCountry={country}
              onCountryClick={handleMapCountryClick}
            />
          </div>
        </section>

        <div className="divider-gold mb-10" />

        <div ref={directoryRef} className="flex items-end justify-between mb-6">
          <div>
            <div className="overline">Searchable Directory</div>
            <h2 className="font-display text-3xl mt-2">Browse the full registry</h2>
          </div>
        </div>

        {!user?.has_complete_access ? (
          <PaywallGate
            title="The full directory is members-only"
            subtitle="The 6 featured resources above are free to browse. Unlock the searchable directory of 200+ international rights agencies — including all PROs, IP authorities, and digital aggregators — for a one-time $6.95."
            ctaLabel="Unlock the directory — $6.95"
          />
        ) : (
          <>
        {/* Category chip filter */}
        <div className="mb-6" data-testid="category-chip-row">
          <div className="flex items-end justify-between mb-3">
            <div className="overline">Filter by category</div>
            <div className="text-xs text-slate-500">
              Tip · open any category as a dedicated page from the link beneath the chip
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setCategory("all")}
              className={`px-3.5 py-2 rounded-md text-xs border transition-all flex items-center gap-2 ${
                category === "all"
                  ? "border-[#7DA3D9] text-[#B8D0EE] bg-[#7DA3D9]/8"
                  : "border-white/10 text-slate-300 hover:border-white/30 bg-white/[0.02]"
              }`}
              data-testid="cat-chip-all"
            >
              <span className="font-medium">All</span>
              <span className="opacity-60">{categoryCounts.total}</span>
            </button>
            {categoryCounts.categories.map((c) => (
              <div key={c.slug} className="flex flex-col items-start gap-1">
                <button
                  onClick={() => setCategory(c.slug)}
                  className={`px-3.5 py-2 rounded-md text-xs border transition-all flex items-center gap-2 ${
                    category === c.slug
                      ? "border-[#7DA3D9] text-[#B8D0EE] bg-[#7DA3D9]/8"
                      : "border-white/10 text-slate-300 hover:border-white/30 bg-white/[0.02]"
                  }`}
                  title={c.blurb}
                  data-testid={`cat-chip-${c.slug}`}
                >
                  <span className="font-medium">{c.label}</span>
                  <span className="opacity-60">{c.count}</span>
                </button>
                <Link
                  to={`/category/${c.slug}`}
                  className="text-[10px] text-slate-500 hover:text-[#B8D0EE] tracking-wide px-1"
                  data-testid={`cat-link-${c.slug}`}
                >
                  Open page →
                </Link>
              </div>
            ))}
          </div>
        </div>

        {/* Filters */}
        <div className="glass-card p-5 mb-8 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <form onSubmit={onSearch} className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              className="input-dark pl-10"
              placeholder="Search 200+ agencies by name or country…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="agencies-search-input"
            />
          </form>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setCountry("all")}
              className={`px-4 py-2 rounded-md text-sm border transition-all ${
                country === "all"
                  ? "border-[#7DA3D9] text-[#7DA3D9] bg-[#7DA3D9]/5"
                  : "border-white/10 text-slate-300 hover:border-white/30"
              }`}
              data-testid="filter-country-all"
            >
              All Countries
            </button>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="input-dark py-2 max-w-[200px]"
              data-testid="agencies-country-select"
            >
              <option value="all" className="bg-[#0A0C10]">By country…</option>
              {countries.map((c) => (
                <option key={c.country_code} value={c.country_code} className="bg-[#0A0C10]">
                  {c.flag} {c.country}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filter pill */}
        {isFiltering && (
          <div className="flex items-center gap-2 mb-6 text-sm text-slate-400">
            <span>Filtered results · </span>
            <button
              onClick={() => { setQ(""); setCountry("all"); setCategory("all"); load(); }}
              className="text-[#7DA3D9] hover:text-[#B8D0EE]"
              data-testid="clear-filters"
            >
              Clear filters
            </button>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-20 overline" data-testid="agencies-loading">Loading registry…</div>
        ) : agencies.length === 0 ? (
          <div className="text-center py-20 text-slate-400" data-testid="agencies-empty">
            No agencies match your search.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="agencies-grid">
            {agencies.map((a) => (
              <AgencyCard key={a.id} agency={a} saved={saved.has(a.id)} onToggleSave={() => toggleSave(a.id)} />
            ))}
          </div>
        )}

        <div className="mt-12 glass-card p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-slate-400 text-sm">
            <Globe2 className="w-4 h-4 text-[#7DA3D9]" />
            <span>
              Showing <span className="text-white font-medium">{agencies.length}</span> of{" "}
              <span className="text-[#7DA3D9] font-medium">{categoryCounts.total} entities</span>{" "}
              across 5 categories
            </span>
          </div>
          <span className="overline text-slate-500">
            Missing your territory? Call <span className="text-[#B8D0EE] notranslate">(310) 905-7115</span>
          </span>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="font-display text-3xl lg:text-4xl text-[#7DA3D9]">{value}</div>
      <div className="overline mt-1">{label}</div>
    </div>
  );
}

function FeaturedCard({ agency, saved, onToggleSave }) {
  return (
    <div
      className="glass-card p-7 fade-up relative overflow-hidden"
      data-testid={`featured-card-${agency.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#7DA3D9] to-transparent" />
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#7DA3D9]/5 rounded-full blur-3xl" />

      <div className="flex items-start justify-between gap-3 relative">
        <div className="flex items-start gap-4">
          {agency.logo_url ? (
            <a
              href={agency.logo_link || agency.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 w-14 h-14 rounded-md bg-white p-1.5 border border-white/10 hover:border-[#7DA3D9] hover:scale-105 transition-all shadow-lg"
              title={`Visit ${agency.name} official website`}
              data-testid={`featured-logo-${agency.id}`}
            >
              <img
                src={agency.logo_url}
                alt={`${agency.name} logo`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${new URL(agency.website).hostname}&sz=128`;
                }}
              />
            </a>
          ) : (
            <div className="text-4xl">{agency.flag || "🌐"}</div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <Star className="w-3 h-3 text-[#7DA3D9]" fill="#7DA3D9" />
              <div className="overline text-[#7DA3D9]">Featured</div>
            </div>
            <h3 className="font-display text-2xl mt-1">{agency.name}</h3>
            <div className="text-xs text-slate-400 mt-0.5">{agency.full_name}</div>
          </div>
        </div>
        <button
          onClick={onToggleSave}
          className="text-slate-400 hover:text-[#7DA3D9] transition-colors p-1"
          aria-label="Save to Favorites"
          data-testid={`save-featured-${agency.id}`}
        >
          {saved ? <BookmarkCheck className="w-5 h-5 text-[#7DA3D9]" /> : <Bookmark className="w-5 h-5" />}
        </button>
      </div>

      <p className="text-sm text-slate-300 mt-5 leading-relaxed line-clamp-3">{agency.description}</p>

      <div className="flex flex-wrap gap-2 mt-4">
        {(agency.rights || []).map((r) => (
          <span key={r} className="px-2.5 py-1 rounded-full text-[10px] border border-[#7DA3D9]/30 bg-[#7DA3D9]/5 text-[#B8D0EE] uppercase tracking-wider">
            {r}
          </span>
        ))}
      </div>

      <div className="divider-gold my-5" />

      <div className="space-y-2.5 text-sm">
        {agency.address && (
          <div className="flex items-start gap-2.5 text-slate-300" data-testid={`featured-address-${agency.id}`}>
            <MapPin className="w-4 h-4 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
            <span className="leading-relaxed">{agency.address}</span>
          </div>
        )}
        {agency.phone && (
          <div className="flex items-center gap-2.5" data-testid={`featured-phone-${agency.id}`}>
            <Phone className="w-4 h-4 text-[#7DA3D9] flex-shrink-0" />
            <a href={`tel:${agency.phone.replace(/\s/g, "")}`} className="text-slate-300 hover:text-[#B8D0EE] transition-colors">
              {agency.phone}
            </a>
          </div>
        )}
        {agency.website && (
          <div className="flex items-center gap-2.5" data-testid={`featured-website-${agency.id}`}>
            <Globe2 className="w-4 h-4 text-[#7DA3D9] flex-shrink-0" />
            <a
              href={agency.website}
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-300 hover:text-[#B8D0EE] transition-colors inline-flex items-center gap-1.5"
            >
              {agency.website.replace(/^https?:\/\/(www\.)?/, "")}
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        )}
      </div>

      <div className="mt-6">
        <Link
          to={`/agencies/${agency.id}`}
          className="text-[#7DA3D9] hover:text-[#B8D0EE] text-sm inline-flex items-center gap-1"
          data-testid={`featured-view-${agency.id}`}
        >
          View full profile →
        </Link>
      </div>
    </div>
  );
}

function AgencyCard({ agency, saved, onToggleSave }) {
  return (
    <div
      className="glass-card p-7 flex flex-col fade-up"
      data-testid={`agency-card-${agency.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-3xl">{agency.flag || "🌐"}</div>
        <button
          onClick={onToggleSave}
          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border transition-all text-xs ${
            saved
              ? "border-[#7DA3D9]/40 bg-[#7DA3D9]/10 text-[#B8D0EE]"
              : "border-white/10 bg-white/[0.02] text-slate-400 hover:border-[#7DA3D9]/40 hover:text-[#7DA3D9] hover:bg-[#7DA3D9]/5"
          }`}
          aria-label="Save to Favorites"
          title={saved ? "In your favorites" : "Save to Favorites"}
          data-testid={`save-agency-${agency.id}`}
        >
          {saved ? (
            <>
              <BookmarkCheck className="w-3.5 h-3.5" /> Saved
            </>
          ) : (
            <>
              <Bookmark className="w-3.5 h-3.5" /> Save
            </>
          )}
        </button>
      </div>
      <Link to={`/agencies/${agency.id}`} className="mt-4 group">
        <h3 className="font-display text-2xl group-hover:text-[#B8D0EE] transition-colors">{agency.name}</h3>
        <div className="text-sm text-slate-400 mt-1">{agency.country} · Est. {agency.founded}</div>
      </Link>
      <p className="text-sm text-slate-300 mt-4 leading-relaxed flex-1 line-clamp-3">{agency.description}</p>

      {(agency.address || agency.phone) && (
        <div className="mt-4 space-y-1.5 text-xs text-slate-400">
          {agency.address && (
            <div className="flex items-start gap-1.5">
              <MapPin className="w-3 h-3 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{agency.address}</span>
            </div>
          )}
          {agency.phone && (
            <div className="flex items-center gap-1.5">
              <Phone className="w-3 h-3 text-[#7DA3D9] flex-shrink-0" />
              <span>{agency.phone}</span>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        {(agency.rights || []).map((r) => (
          <span key={r} className="px-2.5 py-1 rounded-full text-[10px] border border-white/10 bg-white/5 uppercase tracking-wider">
            {r}
          </span>
        ))}
      </div>
      <div className="divider-gold my-5" />
      <div className="flex items-center justify-between text-sm">
        <Link to={`/agencies/${agency.id}`} className="text-[#7DA3D9] hover:text-[#B8D0EE]" data-testid={`view-agency-${agency.id}`}>
          View details →
        </Link>
        <a
          href={agency.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-white inline-flex items-center gap-1.5"
          data-testid={`agency-website-${agency.id}`}
        >
          Website <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </div>
    </div>
  );
}
